import { getConfig } from '@/config';
import { RouteName } from '@/router/models/route-name';
import { setLocationHref } from '@/utils/location-utils';
import {
  AuthError,
  InteractionRequiredAuthError,
  PublicClientApplication,
  type AccountInfo,
  type AuthenticationResult,
  type Configuration,
} from '@azure/msal-browser';
import type { models } from 'cesg-shared';
import { computed, ref, type ComputedRef, type Ref } from 'vue';
import { appHttpService } from '../standard/http-service';

const config = getConfig();

const msalConfig: Configuration = {
  auth: {
    clientId: config.ENTRA_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${config.ENTRA_TENANT_ID}`,
    redirectUri: window.location.origin,
    // Optional: for single-page apps, this helps msal-browser handle hash-based routing.
    // navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: 'localStorage', // 'sessionStorage' or 'localStorage'
    storeAuthStateInCookie: false,
  },
};

const loginRequest = {
  scopes: ['User.Read'],
};

export const apiRequest = {
  scopes: [`api://${config.ENTRA_CLIENT_ID}/API.Access`],
};

class EntraAuthService {
  private msalInstance: PublicClientApplication;

  public activeAccount: Ref<AccountInfo | null>;
  public isLoggedIn: ComputedRef<boolean>;
  public isInitialized: Ref<boolean>;
  public me: Ref<models.GetMeResponse | null>;

  constructor(config: Configuration) {
    this.msalInstance = new PublicClientApplication(config);
    this.me = ref(null);
    this.activeAccount = ref(null);
    this.isInitialized = ref(false);

    this.isLoggedIn = computed(() => !!this.activeAccount.value);
  }

  /**
   * Initializes the service.
   * This should be called once when the app loads (e.g., in main.ts).
   * It handles authentication redirects and sets the initial account state.
   */
  public async initialize(): Promise<void> {
    try {
      await this.msalInstance.initialize();

      // This will resolve with the auth result if the page is loaded after a redirect from the login page.
      const authResult: AuthenticationResult | null =
        await this.msalInstance.handleRedirectPromise();

      if (authResult) {
        this.activeAccount.value = authResult.account;
        // Recorded so the branch below can find it on the next page load — see why that pointer, and
        // not the account list, is the thing to trust.
        this.msalInstance.setActiveAccount(authResult.account);
      } else {
        /*
         * If not a redirect, restore THIS app's account from cache via the active-account pointer.
         *
         * Deliberately not `getAllAccounts()[0]`. msal-browser namespaces token cache keys by clientId
         * (`msal.1.token.keys.<clientId>`) but NOT account keys — the account list lives at the shared,
         * unscoped `msal.1.account.keys` — so both PublicClientApplication instances in this bundle
         * (this one and sharepoint-auth-service.ts's) read one origin-wide list. Index 0 there means
         * "whoever signed in first anywhere on this origin", which in dev is routinely the SharePoint
         * tenant's user.
         *
         * The active-account pointer is the one per-instance account record MSAL keeps: its key goes
         * through generateCacheKey, i.e. `msal.<clientId>.active-account-filters`. So this asks the only
         * question that is actually well-posed — "who did *this* app registration sign in?".
         *
         * And deliberately not a `{ realm: ENTRA_TENANT_ID }` filter either, which looks right and is
         * not: AccountEntity sets `realm` from `clientInfo.utid`, the account's HOME tenant, which says
         * nothing about which authority it signed into. Any user who is a guest in the app's tenant
         * rather than homed in it matches nothing, so login succeeds and is then lost on the very next
         * page load.
         */
        const activeAccount = this.msalInstance.getActiveAccount();
        if (activeAccount) {
          this.activeAccount.value = activeAccount;
        }
      }
      await this.getMe();
    } catch (error) {
      console.error('MSAL initialization error:', error);
    } finally {
      this.isInitialized.value = true;
    }
  }

  /**
   * Logs the user in using a popup window.
   */
  public async login(): Promise<void> {
    try {
      const authResult = await this.msalInstance.loginPopup(loginRequest);
      this.activeAccount.value = authResult.account;
      this.msalInstance.setActiveAccount(authResult.account);
      await this.getMe();
      setLocationHref(`/${RouteName.Projects}`);
    } catch (error) {
      if ((error as AuthError).errorCode === 'user_cancelled') {
        console.log('User cancelled login.');
      } else {
        console.error('Login failed:', error);
      }
    }
  }

  /**
   * Logs the user out using logoutPopup.
   */
  public async logout(): Promise<void> {
    if (!this.activeAccount.value) {
      return;
    }

    try {
      await this.msalInstance.logoutPopup({
        account: this.activeAccount.value,
      });
      this.activeAccount.value = null;
      this.me.value = null;
      setLocationHref('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }

  /**
   * Acquires an access token silently. If interaction is required, it will attempt to acquire it via
   * a popup. THROWS on failure.
   *
   * This is the shared silent→popup body; `acquireToken` below is this method plus
   * `try/catch → null`. Two entry points because their callers need opposite things: an
   * http-service middleware wants a token-or-null it can shrug at, while a caller acquiring a scope the
   * app login never requested — `Mail.ReadWrite` for the Outlook mail tool — must be able to tell
   * "the user declined consent" from "here is a token". A null there would silently become an
   * unauthenticated Graph call reported to the user as some later, unrelated failure.
   *
   * Beware what a caller inside a click gesture is up against: since login only requests `User.Read`,
   * a first `Mail.ReadWrite` request has no cached refresh token for that scope, so MSAL's silent chain
   * fails, tries a hidden iframe (which fails outright with third-party cookies blocked), and lands on
   * `acquireTokenPopup` — i.e. `window.open`. Call this as the FIRST statement of the handler; after an
   * await, transient user activation is gone and Firefox/Safari block the popup. MSAL's
   * `popup_window_error` is not an InteractionRequiredAuthError either, so it surfaces here as a plain
   * rejection rather than something recoverable.
   *
   * The console logging lives in this body rather than in acquireToken's catch, because only here is it
   * known WHICH failure happened — a fatal silent error vs a declined/blocked popup — and that
   * distinction is the only diagnostic either caller gets.
   *
   * @param scopes - Array of scopes to request the token for.
   */
  public async acquireTokenOrThrow(
    scopes: string[] = loginRequest.scopes,
  ): Promise<string> {
    const account = this.activeAccount.value;
    if (!account) {
      console.warn('No active account; cannot acquire token.');
      throw new Error('No active account; cannot acquire token.');
    }

    const tokenRequest = { scopes: scopes, account };

    try {
      const response = await this.msalInstance.acquireTokenSilent(tokenRequest);
      return response.accessToken;
    } catch (error) {
      // If silent fails, try interactive (popup)
      if (!(error instanceof InteractionRequiredAuthError)) {
        console.error('Failed to acquire token:', error);
        throw error;
      }

      console.warn(
        'Silent token acquisition failed. Acquiring token using popup.',
      );
      try {
        const response = await this.msalInstance.acquireTokenPopup(tokenRequest);
        return response.accessToken;
      } catch (popupError) {
        console.error('Failed to acquire token via popup:', popupError);
        throw popupError;
      }
    }
  }

  /**
   * Acquires an access token silently. If interaction is required, it will attempt to acquire it via
   * a popup. Returns null on failure.
   *
   * The null-returning contract is deliberately unchanged and must stay that way:
   * `services/standard/http-service.ts`'s auth middleware depends on it, and every caller of the app
   * API goes through that middleware.
   *
   * @param scopes - Array of scopes to request the token for.
   */
  public async acquireToken(
    scopes: string[] = loginRequest.scopes,
  ): Promise<string | null> {
    try {
      return await this.acquireTokenOrThrow(scopes);
    } catch {
      // Silent by design: acquireTokenOrThrow has already logged (and distinguished) the failure, so
      // logging again here would only double every message.
      return null;
    }
  }

  private async getMe(): Promise<void> {
    if (this.isLoggedIn.value) {
      this.me.value = await appHttpService.request
        .url('/auth/me')
        .get()
        .json<models.GetMeResponse>();
    }
  }
}

export const entraAuthService = new EntraAuthService(msalConfig);
